import React from 'react';
import {
  Send, Filter, Users, ChevronLeft, ChevronDown, Star, AlertTriangle,
  Check, Calendar, Tag, CalendarClock, Trophy, Euro, Ticket, Smartphone,
} from 'lucide-react';
import { ChannelSelector } from '../ChannelSelector';
import { TEMPLATE_ICONS, TIERS, SEGMENTS, SPENDING_SEGMENTS, FREQUENCY_SEGMENTS, PillButton, smsSegments } from './shared';

export function ComposeStep({
  template, customText, setCustomText, ctaText, setCtaText,
  segment, setSegment, selectedTiers, toggleTier, setSelectedTiers,
  minPoints, setMinPoints, minVisits, setMinVisits,
  inactiveDays, setInactiveDays, newDays, setNewDays,
  spendingSegment, setSpendingSegment, frequencySegment, setFrequencySegment,
  showFilters, setShowFilters,
  preview, sending, onBack, onSend,
  formattedHours,
  activeChallenges, selectedChallenge, setSelectedChallenge,
  activePromos, selectedPromo, setSelectedPromo,
  eventName, setEventName,
  activeEvents, selectedEvent, setSelectedEvent,
  activeCoupons, selectedCoupon, setSelectedCoupon,
  selectedChannels, setSelectedChannels, pushSubscribers,
  scheduleMode, setScheduleMode, scheduledDate, setScheduledDate, scheduledTime, setScheduledTime,
  frequencyAlert,
}) {
  const customTextRef = React.useRef(null);
  const insertMergeTag = (tag) => {
    const max = 160;
    const el = customTextRef.current;
    if (!el) { setCustomText((customText + tag).slice(0, max)); return; }
    const start = el.selectionStart ?? customText.length;
    const end = el.selectionEnd ?? customText.length;
    const next = (customText.slice(0, start) + tag + customText.slice(end)).slice(0, max);
    setCustomText(next);
    requestAnimationFrame(() => {
      try { el.focus(); const pos = Math.min(start + tag.length, next.length); el.setSelectionRange(pos, pos); } catch (e) { /* noop */ }
    });
  };
  const Icon = TEMPLATE_ICONS[template.icon] || Star;
  const hasActiveFilters = segment || selectedTiers.length > 0 || minPoints || minVisits || spendingSegment || frequencySegment;
  const isAutoHours = template.auto_hours;
  const isAutoChallenge = template.auto_challenge;
  const isAutoPromo = template.auto_promo;
  const isAutoMenu = template.auto_menu;
  const isAutoEvento = template.auto_evento;
  const isAutoSpecialHours = template.auto_special_hours;
  const isAutoCoupon = template.auto_coupon;

  const canSend = isAutoHours ? !!formattedHours : isAutoChallenge ? !!selectedChallenge : isAutoPromo ? !!selectedPromo : isAutoEvento ? (!!eventName?.trim() && !!customText.trim()) : isAutoSpecialHours ? !!customText.trim() : isAutoCoupon ? !!selectedCoupon : !!customText.trim();

  // SMS segment/cost estimate. The SMS body that goes out is the free text
  // the admin controls (no "Ciao {nome}" prefix is added for SMS), so this
  // reflects the real per-message cost the carrier will bill.
  const smsSelected = (selectedChannels || []).includes('sms');
  // Estimate on the expanded text (merge tags are replaced at send time).
  const smsEst = smsSelected ? smsSegments((customText || '').replace(/\{nome\}/g, 'Alessandro').replace(/\{locale\}/g, 'Il Locale')) : null;

  return (
    <div className="space-y-4" data-testid="compose-step">
      <div className="flex items-center gap-3">
        <button onClick={onBack} data-testid="back-to-templates" className="w-8 h-8 rounded-xl bg-slate-100 flex items-center justify-center text-[#64748B] hover:bg-gray-200 transition-colors">
          <ChevronLeft size={16} />
        </button>
        <div className="w-8 h-8 rounded-xl bg-[#D44E20]/10 flex items-center justify-center">
          <Icon size={14} className="text-[#D44E20]" />
        </div>
        <div>
          <p className="font-semibold text-[#1A1A1A] text-sm">{template.name}</p>
          <p className="text-[#999] text-[11px]">{template.description}</p>
        </div>
      </div>

      {/* Universal channel-aware constraint banner — shown above ALL template branches.
          Helps the brand-admin understand WHY they cannot type a free message in
          WhatsApp templates: every approved template has fixed copy and only
          specific placeholders can be edited. */}
      {(() => {
        const isCustom = template.id === 'custom';
        const wa = (selectedChannels || []).includes('whatsapp');
        if (isCustom) {
          return (
            <div className="mb-4 px-3 py-2.5 bg-blue-50 border border-blue-200 rounded-xl flex items-start gap-2" data-testid="compose-banner-custom">
              <Star size={12} className="text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-[11px] leading-relaxed text-blue-900">
                <span className="font-semibold">Messaggio libero — solo SMS e Push.</span> WhatsApp richiede un template approvato da Meta: scegli uno dei template della libreria per inviare via WhatsApp.
              </p>
            </div>
          );
        }
        if (wa) {
          return (
            <div className="mb-4 px-3 py-2.5 bg-emerald-50 border border-emerald-200 rounded-xl flex items-start gap-2" data-testid="compose-banner-whatsapp">
              <Check size={12} className="text-emerald-600 flex-shrink-0 mt-0.5" />
              <div className="text-[11px] leading-relaxed text-emerald-900">
                <span className="font-semibold">Template WhatsApp approvato da Meta.</span> Puoi compilare solo i campi modificabili sotto: il resto del messaggio è fisso. Per messaggi liberi attiva solo SMS o Push e usa il template &ldquo;Crea nuovo&rdquo;.
              </div>
            </div>
          );
        }
        return (
          <div className="mb-4 px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl flex items-start gap-2" data-testid="compose-banner-freetext">
            <Star size={12} className="text-slate-500 flex-shrink-0 mt-0.5" />
            <p className="text-[11px] leading-relaxed text-slate-700">
              <span className="font-semibold">Testo libero (SMS/Push).</span> Massimo 160 caratteri.
            </p>
          </div>
        );
      })()}

      {isAutoHours ? (
        <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm" data-testid="auto-hours-compose">
          <h3 className="font-semibold text-[#1A1A1A] text-sm mb-1">Anteprima messaggio</h3>
          <p className="text-[#999] text-[10px] mb-4">Gli orari standard vengono inseriti automaticamente dalla configurazione del locale.</p>
          {formattedHours ? (
            <div className="bg-[#E7F8EC] border border-[#C8E6C9] rounded-xl p-4" data-testid="hours-auto-preview">
              <p className="text-[10px] text-[#4CAF50] font-medium mb-2 flex items-center gap-1"><Check size={10} /> Orari standard configurati</p>
              <p className="text-[12px] text-[#1B5E20] whitespace-pre-line font-mono">{formattedHours}</p>
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="hours-missing-warning">
              <p className="text-amber-700 text-xs flex items-center gap-1.5"><AlertTriangle size={14} /> Configura prima gli orari standard nella sezione "Locale"</p>
            </div>
          )}
        </div>

      ) : isAutoChallenge ? (
        <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm" data-testid="auto-challenge-compose">
          <h3 className="font-semibold text-[#1A1A1A] text-sm mb-1">Seleziona la Challenge</h3>
          <p className="text-[#999] text-[10px] mb-4">Scegli quale challenge attiva vuoi annunciare.</p>
          {(!activeChallenges || activeChallenges.length === 0) ? (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <p className="text-amber-700 text-xs flex items-center gap-1.5"><AlertTriangle size={14} /> Nessuna challenge attiva.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {activeChallenges.map(ch => (
                <button key={ch.id} onClick={() => setSelectedChallenge(ch.id === selectedChallenge ? null : ch.id)}
                  data-testid={`challenge-option-${ch.id}`}
                  className={`w-full text-left p-3.5 rounded-xl border-2 transition-all ${selectedChallenge === ch.id ? 'border-[#D44E20] bg-[#D44E20]/5 shadow-sm' : 'border-slate-100 bg-slate-50 hover:border-slate-200'}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold text-[#1A1A1A] text-sm flex items-center gap-1.5">
                      <Trophy size={13} className={selectedChallenge === ch.id ? 'text-[#D44E20]' : 'text-[#aaa]'} /> {ch.name}
                    </span>
                    {selectedChallenge === ch.id && <span className="w-5 h-5 rounded-full bg-[#D44E20] flex items-center justify-center"><Check size={11} className="text-white" /></span>}
                  </div>
                  <p className="text-[#64748B] text-xs ml-5">{ch.description}</p>
                  <div className="flex items-center gap-3 mt-1.5 ml-5">
                    {ch.reward_label && <span className="text-[10px] text-[#D44E20] font-medium bg-[#D44E20]/8 px-2 py-0.5 rounded-md">{ch.reward_label}</span>}
                    {ch.end_date ? <span className="text-[10px] text-[#64748B] flex items-center gap-0.5"><Calendar size={9} /> Scade: {ch.end_date}</span> : <span className="text-[10px] text-[#aaa] italic">Nessuna scadenza</span>}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

      ) : isAutoPromo ? (
        <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm" data-testid="auto-promo-compose">
          <h3 className="font-semibold text-[#1A1A1A] text-sm mb-1">Seleziona la Promozione</h3>
          <p className="text-[#999] text-[10px] mb-4">Scegli quale promozione attiva vuoi comunicare.</p>
          {(!activePromos || activePromos.length === 0) ? (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <p className="text-amber-700 text-xs flex items-center gap-1.5"><AlertTriangle size={14} /> Nessuna promozione attiva.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {activePromos.map(pr => (
                <button key={pr.id} onClick={() => setSelectedPromo(pr.id === selectedPromo ? null : pr.id)}
                  data-testid={`promo-option-${pr.id}`}
                  className={`w-full text-left p-3.5 rounded-xl border-2 transition-all ${selectedPromo === pr.id ? 'border-[#D44E20] bg-[#D44E20]/5 shadow-sm' : 'border-slate-100 bg-slate-50 hover:border-slate-200'}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold text-[#1A1A1A] text-sm flex items-center gap-1.5">
                      <Tag size={13} className={selectedPromo === pr.id ? 'text-[#D44E20]' : 'text-[#aaa]'} /> {pr.name}
                    </span>
                    {selectedPromo === pr.id && <span className="w-5 h-5 rounded-full bg-[#D44E20] flex items-center justify-center"><Check size={11} className="text-white" /></span>}
                  </div>
                  <p className="text-[#64748B] text-xs ml-5 line-clamp-2">{pr.description}</p>
                  <div className="flex items-center gap-3 mt-1.5 ml-5">
                    {(pr.valid_from || pr.valid_to) && <span className="text-[10px] text-[#64748B] flex items-center gap-0.5"><Calendar size={9} /> {pr.valid_from || ''}{pr.valid_from && pr.valid_to ? ' - ' : ''}{pr.valid_to || ''}</span>}
                    {pr.conditions && <span className="text-[10px] text-[#aaa]">{pr.conditions}</span>}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

      ) : isAutoEvento ? (
        <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm" data-testid="auto-evento-compose">
          <h3 className="font-semibold text-[#1A1A1A] text-sm mb-1">Seleziona o descrivi l'evento</h3>
          <p className="text-[#999] text-[10px] mb-4">Il nome del cliente viene inserito automaticamente.</p>
          {activeEvents && activeEvents.length > 0 && (
            <div className="mb-4">
              <label className="text-[11px] text-[#64748B] font-medium block mb-2">Eventi salvati</label>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {activeEvents.map(ev => (
                  <button key={ev.id} onClick={() => {
                    if (selectedEvent === ev.id) { setSelectedEvent(null); setEventName(''); setCustomText(''); }
                    else { setSelectedEvent(ev.id); setEventName(ev.name); setCustomText(ev.description); }
                  }}
                    data-testid={`select-event-${ev.id}`}
                    className={`w-full text-left p-3 rounded-xl border transition-all ${selectedEvent === ev.id ? 'border-[#D44E20] bg-[#D44E20]/5' : 'border-slate-100 hover:border-slate-200'}`}>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-xs text-[#1A1A1A]">{ev.name}</span>
                      {ev.is_recurring && <span className="text-[8px] font-bold bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">RICORRENTE</span>}
                      {ev.event_date && <span className="text-[10px] text-[#999]">{ev.event_date}</span>}
                    </div>
                    <p className="text-[#64748B] text-[10px] line-clamp-2 mt-0.5">{ev.description}</p>
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 mt-2 mb-3">
                <div className="flex-1 h-px bg-slate-100" /><span className="text-[10px] text-[#bbb]">oppure scrivi manualmente</span><div className="flex-1 h-px bg-slate-100" />
              </div>
            </div>
          )}
          <div className="mb-4">
            <label className="text-[11px] text-[#64748B] font-medium block mb-1">Nome evento <span className="text-[#D44E20]">*</span></label>
            <input type="text" value={eventName} onChange={e => { setEventName(e.target.value.slice(0, 100)); setSelectedEvent(null); }} maxLength={100}
              placeholder="Es. Serata Jazz dal vivo" data-testid="broadcast-event-name"
              className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] placeholder:text-[#ccc] focus:border-[#D44E20] focus:outline-none transition-colors" />
            <div className="flex justify-end mt-1"><span className={`text-[10px] font-medium ${(eventName?.length || 0) > 80 ? 'text-amber-500' : 'text-[#999]'}`}>{eventName?.length || 0}/100</span></div>
          </div>
          <div>
            <label className="text-[11px] text-[#64748B] font-medium block mb-1">Dettagli evento <span className="text-[#D44E20]">*</span></label>
            <textarea value={customText} onChange={e => { setCustomText(e.target.value.slice(0, 500)); setSelectedEvent(null); }} maxLength={500}
              placeholder={"Es.\nVenerdi 28 marzo, dalle 21:00\nIngresso libero con consumazione"} rows={5} data-testid="broadcast-event-details"
              className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-sm text-[#1A1A1A] placeholder:text-[#ccc] focus:border-[#D44E20] focus:outline-none resize-none transition-colors" />
            <div className="flex justify-between mt-1">
              <span className="text-[10px] text-[#999]">Data, ora, dettagli, condizioni...</span>
              <span className={`text-[10px] font-medium ${customText.length > 450 ? 'text-amber-500' : 'text-[#999]'}`}>{customText.length}/500</span>
            </div>
          </div>
        </div>

      ) : isAutoMenu ? (
        <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm" data-testid="auto-menu-compose">
          <h3 className="font-semibold text-[#1A1A1A] text-sm mb-1">Scrivi le novit{'\u00E0'} del menu</h3>
          <p className="text-[#999] text-[10px] mb-4">Nome cliente e locale vengono inseriti automaticamente.</p>
          <textarea value={customText} onChange={e => setCustomText(e.target.value.slice(0, 500))} maxLength={500}
            placeholder={"Es.\n\uD83C\uDF5D Nuovo risotto allo zafferano\n\uD83C\uDF79 Cocktail stagionali"} rows={6} data-testid="broadcast-menu-text"
            className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-sm text-[#1A1A1A] placeholder:text-[#ccc] focus:border-[#D44E20] focus:outline-none resize-none transition-colors" />
          <div className="flex justify-between mt-1.5">
            <span className="text-[10px] text-[#999]">Piatti, drink, novit{'\u00E0'} del menu...</span>
            <span className={`text-[10px] font-medium ${customText.length > 450 ? 'text-amber-500' : 'text-[#999]'}`}>{customText.length}/500</span>
          </div>
        </div>

      ) : isAutoSpecialHours ? (
        <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm" data-testid="auto-special-hours-compose">
          <h3 className="font-semibold text-[#1A1A1A] text-sm mb-1">Orari Speciali</h3>
          <p className="text-[#999] text-[10px] mb-4">Comunica aperture straordinarie, chiusure eccezionali, variazioni temporanee. Il nome cliente viene inserito automaticamente.</p>
          <textarea value={customText} onChange={e => setCustomText(e.target.value.slice(0, 500))} maxLength={500}
            placeholder={"Es.\nDomani apertura straordinaria dalle 18 alle 2!\nVi aspettiamo per una serata speciale."} rows={5} data-testid="broadcast-special-hours-text"
            className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-sm text-[#1A1A1A] placeholder:text-[#ccc] focus:border-[#D44E20] focus:outline-none resize-none transition-colors" />
          <div className="flex justify-between mt-1.5">
            <span className="text-[10px] text-[#999]">Orari, aperture, chiusure speciali...</span>
            <span className={`text-[10px] font-medium ${customText.length > 450 ? 'text-amber-500' : 'text-[#999]'}`}>{customText.length}/500</span>
          </div>
        </div>

      ) : isAutoCoupon ? (
        <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm" data-testid="auto-coupon-compose">
          <h3 className="font-semibold text-[#1A1A1A] text-sm mb-1">Seleziona il Coupon</h3>
          <p className="text-[#999] text-[10px] mb-4">Scegli quale coupon attivo inviare. Codice, premio e scadenza vengono inseriti automaticamente.</p>
          {(!activeCoupons || activeCoupons.length === 0) ? (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <p className="text-amber-700 text-xs flex items-center gap-1.5"><AlertTriangle size={14} /> Nessun coupon attivo. Creane uno nella sezione Coupon.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {activeCoupons.map(cp => (
                <button key={cp.id} onClick={() => setSelectedCoupon(cp.id === selectedCoupon ? null : cp.id)}
                  data-testid={`coupon-option-${cp.id}`}
                  className={`w-full text-left p-3.5 rounded-xl border-2 transition-all ${selectedCoupon === cp.id ? 'border-[#D44E20] bg-[#D44E20]/5 shadow-sm' : 'border-slate-100 bg-slate-50 hover:border-slate-200'}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold text-[#1A1A1A] text-sm flex items-center gap-1.5">
                      <Ticket size={13} className={selectedCoupon === cp.id ? 'text-[#D44E20]' : 'text-[#aaa]'} /> {cp.code}
                    </span>
                    {selectedCoupon === cp.id && <span className="w-5 h-5 rounded-full bg-[#D44E20] flex items-center justify-center"><Check size={11} className="text-white" /></span>}
                  </div>
                  <p className="text-[#64748B] text-xs ml-5">{cp.reward_label}</p>
                  <div className="flex items-center gap-3 mt-1.5 ml-5">
                    <span className="text-[10px] text-[#D44E20] font-medium bg-[#D44E20]/8 px-2 py-0.5 rounded-md">{cp.coupon_type}</span>
                    {cp.valid_until ? <span className="text-[10px] text-[#64748B] flex items-center gap-0.5"><Calendar size={9} /> Scade: {cp.valid_until}</span> : <span className="text-[10px] text-[#aaa] italic">Senza scadenza</span>}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

      ) : (
        <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm" data-testid="freeform-compose">
          <h3 className="font-semibold text-[#1A1A1A] text-sm mb-1">Componi il messaggio</h3>
          <p className="text-[#999] text-[10px] mb-3">Usa i tag cliccabili qui sotto per personalizzare: verranno sostituiti col nome del cliente e del locale per ogni destinatario.</p>

          <div className="flex flex-wrap gap-1.5 mb-4" data-testid="merge-tags">
            <button type="button" onClick={() => insertMergeTag('{nome}')} data-testid="merge-tag-nome"
              className="text-[10px] px-2 py-1 rounded-md bg-blue-50 text-blue-600 font-medium border border-blue-100 hover:bg-blue-100 transition-colors">
              + {'{nome}'} Nome cliente
            </button>
            <button type="button" onClick={() => insertMergeTag('{locale}')} data-testid="merge-tag-locale"
              className="text-[10px] px-2 py-1 rounded-md bg-blue-50 text-blue-600 font-medium border border-blue-100 hover:bg-blue-100 transition-colors">
              + {'{locale}'} Nome locale
            </button>
          </div>
          <div className="mb-4">
            <label className="text-[11px] text-[#64748B] font-medium block mb-1">
              {template.id === 'custom' ? 'Testo libero del messaggio' : 'Testo da inserire nel template'}
              <span className="text-[#D44E20]"> *</span>
            </label>
            <textarea value={customText} onChange={e => setCustomText(e.target.value.slice(0, 160))} maxLength={160}
              ref={customTextRef}
              placeholder="Es. Venerdì sera live music con DJ Marco!" rows={3} data-testid="broadcast-custom-text"
              className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-sm text-[#1A1A1A] placeholder:text-[#ccc] focus:border-[#D44E20] focus:outline-none resize-none transition-colors" />
            <div className="flex justify-between mt-1">
              <span className="text-[10px] text-[#999]">Descrizione evento, promo, novit{'\u00E0'}...</span>
              <span className={`text-[10px] font-medium ${customText.length > 140 ? 'text-amber-500' : 'text-[#999]'}`}>{customText.length}/160</span>
            </div>
          </div>

          {/* Full template body preview with placeholder highlight (only for non-custom) */}
          {template.id !== 'custom' && template.body && (
            <details className="mb-4 rounded-xl border border-slate-100 bg-slate-50/40 overflow-hidden" data-testid="full-template-body">
              <summary className="cursor-pointer px-3 py-2 text-[10px] font-semibold text-[#64748B] hover:text-[#1A1A1A] select-none">
                Mostra il template completo →
              </summary>
              <div className="px-3 pb-3 pt-1">
                <pre className="text-[11px] text-[#1A1A1A] whitespace-pre-wrap font-sans leading-relaxed">
                  {template.body.split(/(\{[123]\})/).map((chunk, i) => {
                    if (/^\{[12]\}$/.test(chunk)) {
                      const label = chunk === '{1}' ? 'Nome cliente' : 'Nome locale';
                      return <span key={i} className="bg-blue-100 text-blue-700 px-1 rounded font-mono text-[10px]">{label}</span>;
                    }
                    if (chunk === '{3}') {
                      return <span key={i} className="bg-amber-100 text-amber-800 px-1 rounded font-mono text-[10px] font-semibold">↑ il tuo testo qui</span>;
                    }
                    return chunk;
                  })}
                </pre>
              </div>
            </details>
          )}

          <div>
            <label className="text-[11px] text-[#64748B] font-medium block mb-1.5">Call to action</label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {template.cta_suggestions?.map(cta => (
                <button key={cta} onClick={() => setCtaText(cta)} data-testid={`cta-${cta.toLowerCase().replace(/\s/g, '-')}`}
                  className={`text-[10px] px-2.5 py-1.5 rounded-lg font-medium border transition-all ${ctaText === cta ? 'bg-[#D44E20] text-white border-[#D44E20]' : 'bg-white text-[#64748B] border-slate-200 hover:border-gray-300'}`}>
                  {cta}
                </button>
              ))}
            </div>
            <input type="text" value={ctaText} onChange={e => setCtaText(e.target.value)} placeholder="Oppure scrivi un CTA personalizzato..." maxLength={40} data-testid="broadcast-cta-text"
              className="w-full h-9 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none transition-colors" />
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <button onClick={() => setShowFilters(!showFilters)} data-testid="toggle-filters" className="w-full flex items-center justify-between p-4">
          <div className="flex items-center gap-2">
            <Filter size={14} className="text-[#D44E20]" />
            <span className="font-semibold text-[#1A1A1A] text-sm">Filtri destinatari</span>
            {hasActiveFilters && <span className="w-1.5 h-1.5 rounded-full bg-[#D44E20]" />}
            {preview !== null && <span className="text-[11px] text-[#64748B] flex items-center gap-1 ml-1 bg-slate-50 px-2 py-0.5 rounded-md"><Users size={10} /> {preview}</span>}
            {preview !== null && preview > 0 && <span className="text-[11px] text-emerald-700 flex items-center gap-1 bg-emerald-50 px-2 py-0.5 rounded-md" data-testid="broadcast-cost-badge"><Euro size={9} /> ~{(preview * 0.0581).toFixed(2)} EUR</span>}
          </div>
          <ChevronDown size={14} className={`text-[#aaa] transition-transform duration-200 ${showFilters ? 'rotate-180' : ''}`} />
        </button>
        {showFilters && (
          <div className="px-4 pb-4 space-y-4 border-t border-gray-50 pt-3">
            <div>
              <label className="text-[10px] text-[#999] font-medium block mb-1.5">Segmento</label>
              <div className="flex flex-wrap gap-1.5">
                {SEGMENTS.map(s => <PillButton key={s.value} active={segment === s.value} onClick={() => setSegment(s.value)} testId={`segment-${s.value || 'all'}`}><s.icon size={11} /> {s.label}</PillButton>)}
              </div>
              {segment === 'inactive' && (
                <div className="mt-2 flex items-center gap-2"><span className="text-[10px] text-[#999]">Inattivi da</span>
                  <input type="number" value={inactiveDays} onChange={e => setInactiveDays(e.target.value)} data-testid="inactive-days-input" className="w-14 h-7 bg-slate-50 border border-slate-200 rounded-lg px-2 text-[10px] text-center focus:border-[#D44E20] focus:outline-none" /><span className="text-[10px] text-[#999]">giorni</span></div>
              )}
              {segment === 'new' && (
                <div className="mt-2 flex items-center gap-2"><span className="text-[10px] text-[#999]">Ultimi</span>
                  <input type="number" value={newDays} onChange={e => setNewDays(e.target.value)} data-testid="new-days-input" className="w-14 h-7 bg-slate-50 border border-slate-200 rounded-lg px-2 text-[10px] text-center focus:border-[#D44E20] focus:outline-none" /><span className="text-[10px] text-[#999]">giorni</span></div>
              )}
            </div>
            <div>
              <label className="text-[10px] text-[#999] font-medium block mb-1.5">Tier</label>
              <div className="flex flex-wrap gap-1.5">
                {TIERS.map(t => <PillButton key={t.value} active={selectedTiers.includes(t.value)} onClick={() => toggleTier(t.value)} testId={`tier-btn-${t.value}`}>{t.label}</PillButton>)}
                {selectedTiers.length > 0 && <button onClick={() => setSelectedTiers([])} className="text-[10px] text-[#999] hover:text-[#D44E20] px-1 transition-colors">Reset</button>}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="text-[10px] text-[#999] font-medium block mb-1.5">Spesa</label>
                <div className="flex flex-wrap gap-1.5">{SPENDING_SEGMENTS.map(s => <PillButton key={s.value} active={spendingSegment === s.value} onClick={() => setSpendingSegment(s.value)} testId={`spending-${s.value || 'all'}`}>{s.icon && <s.icon size={10} />} {s.label}</PillButton>)}</div></div>
              <div><label className="text-[10px] text-[#999] font-medium block mb-1.5">Frequenza</label>
                <div className="flex flex-wrap gap-1.5">{FREQUENCY_SEGMENTS.map(s => <PillButton key={s.value} active={frequencySegment === s.value} onClick={() => setFrequencySegment(s.value)} testId={`frequency-${s.value || 'all'}`}>{s.icon && <s.icon size={10} />} {s.label}</PillButton>)}</div></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-[10px] text-[#999] font-medium block mb-1">Punti min.</label>
                <input type="number" value={minPoints} onChange={e => setMinPoints(e.target.value)} placeholder="0" data-testid="broadcast-min-points" className="w-full h-8 bg-slate-50 border border-slate-200 rounded-lg px-2.5 text-xs text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" /></div>
              <div><label className="text-[10px] text-[#999] font-medium block mb-1">Visite min.</label>
                <input type="number" value={minVisits} onChange={e => setMinVisits(e.target.value)} placeholder="0" data-testid="broadcast-min-visits" className="w-full h-8 bg-slate-50 border border-slate-200 rounded-lg px-2.5 text-xs text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" /></div>
            </div>
          </div>
        )}
      </div>

      {/* Meta Frequency Alert */}
      {frequencyAlert && (
        <div className={`rounded-2xl p-4 border shadow-sm ${
          frequencyAlert.avg_messages_per_user >= frequencyAlert.meta_limit ? 'bg-red-50 border-red-200'
            : frequencyAlert.avg_messages_per_user >= frequencyAlert.meta_limit - 1 ? 'bg-amber-50 border-amber-200'
            : 'bg-green-50 border-green-100'
        }`} data-testid="meta-frequency-alert">
          <div className="flex items-start gap-2.5">
            {frequencyAlert.avg_messages_per_user >= frequencyAlert.meta_limit ? <AlertTriangle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
              : frequencyAlert.avg_messages_per_user >= frequencyAlert.meta_limit - 1 ? <AlertTriangle size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
              : <Check size={16} className="text-green-600 flex-shrink-0 mt-0.5" />}
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-xs font-bold ${frequencyAlert.avg_messages_per_user >= frequencyAlert.meta_limit ? 'text-red-700' : frequencyAlert.avg_messages_per_user >= frequencyAlert.meta_limit - 1 ? 'text-amber-700' : 'text-green-700'}`}>
                  Media {frequencyAlert.avg_messages_per_user} msg/utente
                </span>
                <span className="text-[10px] text-[#999]">ultimi 30 giorni</span>
              </div>
              <p className={`text-[10px] mt-0.5 ${frequencyAlert.avg_messages_per_user >= frequencyAlert.meta_limit ? 'text-red-600' : frequencyAlert.avg_messages_per_user >= frequencyAlert.meta_limit - 1 ? 'text-amber-600' : 'text-green-600'}`}>
                {frequencyAlert.avg_messages_per_user >= frequencyAlert.meta_limit ? `Sopra soglia Meta (max ${frequencyAlert.meta_limit})! Rischio limitazioni.`
                  : frequencyAlert.avg_messages_per_user >= frequencyAlert.meta_limit - 1 ? `Vicino alla soglia Meta (max ${frequencyAlert.meta_limit}). Attenzione.`
                  : `Sotto soglia Meta (max ${frequencyAlert.meta_limit}). Account in salute.`}
              </p>
              <div className="flex items-center gap-3 mt-1.5">
                <span className="text-[9px] text-[#64748B]">{frequencyAlert.broadcasts_30d} broadcast</span>
                <span className="text-[9px] text-[#64748B]">{frequencyAlert.automations_30d} automazioni</span>
                <span className="text-[9px] text-[#64748B]">{frequencyAlert.total_marketing_customers} utenti marketing</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Channel Selection */}
      <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm">
        <ChannelSelector
          selected={selectedChannels} onChange={setSelectedChannels}
          recipientCount={preview || 0} pushSubscribers={pushSubscribers || 0}
          whatsappLocked={template.id === 'custom'}
          whatsappLockReason="Il template &ldquo;Crea nuovo&rdquo; è un messaggio libero, non disponibile su WhatsApp (Meta richiede un template approvato). Disponibile su SMS e Push."
        />

        {smsEst && customText.trim() && (
          <div className={`mt-3 rounded-xl p-3 border text-[11px] ${smsEst.segments > 1 ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'}`} data-testid="sms-segment-estimate">
            <div className="flex items-center gap-1.5 font-semibold text-[#1A1A1A]">
              <Smartphone size={12} className={smsEst.segments > 1 ? 'text-amber-600' : 'text-[#64748B]'} />
              SMS: {smsEst.chars} caratteri · {smsEst.segments} {smsEst.segments > 1 ? 'parti' : 'parte'} per destinatario
              {preview > 0 && <span className="ml-1 text-[#64748B]">· costo reale ~€{(preview * 0.0395 * smsEst.segments).toFixed(2)}</span>}
            </div>
            {smsEst.segments > 1 && (
              <p className="mt-1 text-amber-800 leading-relaxed">
                <AlertTriangle size={11} className="inline mr-1 -mt-0.5 text-amber-600" />
                Il messaggio supera {smsEst.limitPerPart} caratteri{smsEst.encoding === 'UCS-2' ? ' (contiene simboli/emoji che riducono il limite a 70)' : ''}: ogni SMS verrà diviso in <span className="font-bold">{smsEst.segments} parti</span> e pagherai <span className="font-bold">{smsEst.segments}× per ogni destinatario</span>. Accorcia il testo per pagare 1 sola volta.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Schedule / Send */}
      <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm space-y-3" data-testid="schedule-section">
        <div className="flex gap-2">
          <button onClick={() => setScheduleMode('now')} data-testid="schedule-mode-now"
            className={`flex-1 h-9 rounded-xl text-xs font-medium flex items-center justify-center gap-1.5 border transition-all ${scheduleMode === 'now' ? 'bg-[#D44E20] text-white border-[#D44E20]' : 'bg-gray-50 text-[#64748B] border-slate-200 hover:border-gray-300'}`}>
            <Send size={12} /> Invia subito
          </button>
          <button onClick={() => setScheduleMode('scheduled')} data-testid="schedule-mode-scheduled"
            className={`flex-1 h-9 rounded-xl text-xs font-medium flex items-center justify-center gap-1.5 border transition-all ${scheduleMode === 'scheduled' ? 'bg-[#D44E20] text-white border-[#D44E20]' : 'bg-gray-50 text-[#64748B] border-slate-200 hover:border-gray-300'}`}>
            <CalendarClock size={12} /> Programma invio
          </button>
        </div>
        {scheduleMode === 'scheduled' && (
          <div className="space-y-2 pt-1">
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-[10px] text-[#64748B] font-medium block mb-1">Data invio</label>
                <input type="date" value={scheduledDate} onChange={e => setScheduledDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]} max={new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                  data-testid="scheduled-date-input" className="w-full h-9 bg-slate-50 border border-slate-200 rounded-xl px-3 text-xs text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" /></div>
              <div><label className="text-[10px] text-[#64748B] font-medium block mb-1">Orario (UTC)</label>
                <input type="time" value={scheduledTime} onChange={e => setScheduledTime(e.target.value)} data-testid="scheduled-time-input"
                  className="w-full h-9 bg-slate-50 border border-slate-200 rounded-xl px-3 text-xs text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" /></div>
            </div>
            <p className="text-[10px] text-[#999]">Il broadcast verra' inviato automaticamente alla data e ora indicata. Max 30 giorni.</p>
          </div>
        )}
        <button onClick={onSend} disabled={!canSend || sending || (scheduleMode === 'scheduled' && (!scheduledDate || !scheduledTime))}
          data-testid="broadcast-send-btn"
          className="w-full h-11 bg-[#D44E20] text-white font-bold rounded-xl flex items-center justify-center gap-2 disabled:opacity-40 hover:bg-[#C04018] transition-colors shadow-sm">
          {sending ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            : scheduleMode === 'scheduled' ? <CalendarClock size={15} /> : <Send size={15} />}
          {sending ? (scheduleMode === 'scheduled' ? 'Programmazione...' : 'Invio in corso...')
            : scheduleMode === 'scheduled' ? `Programma broadcast${preview !== null ? ` per ${preview} clienti` : ''}` : `Invia broadcast${preview !== null ? ` a ${preview} clienti` : ''}`}
        </button>
      </div>
    </div>
  );
}
